/**
 * MED-781: Coach Home Dashboard.
 *
 * Layout (top-to-bottom):
 *   1. Welcome header
 *   2. Needs Attention rail (horizontal scroll, prioritized cards)
 *   3. Active Cohorts compact list
 *   4. Two-column row: Availability Requests (left) + Upcoming Sessions (right)
 *
 * The previous "This Month" stats panel and "Action Items" panel are removed
 * (AC3, AC4); their data sources now feed the Needs Attention rail cards.
 *
 * The page is a thin orchestrator (Page → Feature → UI per Medley coding
 * principles). Data fetching lives here; presentation lives in the dashboard
 * subcomponents under `components/dashboard/`.
 */

import { useEffect, useState, useCallback } from 'react';
import { Container, Grid, Stack, Box, Title, Text, Center, Loader } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { coachPortalApi } from '../../services/api/coachPortal';
import type {
  CoachAvailabilityRequest,
  CoachCohort,
  CoachUpcomingSession,
  CoachRecentCompletedSession,
} from '../../services/api/coachPortal';
import { NeedsAttentionRail } from './components/dashboard/NeedsAttentionRail';
import { ActiveCohortsCompactList } from './components/dashboard/ActiveCohortsCompactList';
import { AvailabilityRequestsPanel } from './components/dashboard/AvailabilityRequestsPanel';
import { UpcomingSessionsPanel } from './components/dashboard/UpcomingSessionsPanel';
import { useCoachActionItems } from './hooks/useCoachActionItems';
import { useNeedsAttentionItems } from './hooks/useNeedsAttentionItems';
import type { NeedsAttentionItem } from './components/dashboard/needsAttentionTypes';
import { missClaimsApi } from '../../services/api/missClaims';
import { openMissClaimQuestion } from '../../components/coaches/missClaimQuestion';
import type { MissClaim } from '../../types';

export function CoachDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const firstName = user?.first_name || 'Coach';
  const isPartnerCoach = user?.coach_type === 'partner';

  const [requests, setRequests] = useState<CoachAvailabilityRequest[]>([]);
  const [cohorts, setCohorts] = useState<CoachCohort[]>([]);
  const [sessions, setSessions] = useState<CoachUpcomingSession[]>([]);
  const [recentCompleted, setRecentCompleted] = useState<CoachRecentCompletedSession | null>(
    null,
  );
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(true);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  // MED-1346: late changes that wait for the coach's answer.
  const [missClaims, setMissClaims] = useState<MissClaim[]>([]);

  const fetchRequests = useCallback(async () => {
    try {
      setIsLoadingRequests(true);
      const data = await coachPortalApi.getAvailabilityRequests();
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch availability requests:', error);
    } finally {
      setIsLoadingRequests(false);
    }
  }, []);

  const fetchCohorts = useCallback(async () => {
    try {
      setIsLoadingCohorts(true);
      const data = await coachPortalApi.getCohorts();
      setCohorts(data);
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
    } finally {
      setIsLoadingCohorts(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      setIsLoadingSessions(true);
      const data = await coachPortalApi.getUpcomingSessions();
      setSessions(data);
    } catch (error) {
      console.error('Failed to fetch upcoming sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const fetchRecentCompleted = useCallback(async () => {
    try {
      setIsLoadingRecent(true);
      const data = await coachPortalApi.getMostRecentCompletedSession();
      setRecentCompleted(data);
    } catch (error) {
      console.error('Failed to fetch most recent completed session:', error);
    } finally {
      setIsLoadingRecent(false);
    }
  }, []);

  // The rail does not wait for this list: a failed load only hides the cards.
  const fetchMissClaims = useCallback(async () => {
    try {
      const data = await missClaimsApi.listPending();
      setMissClaims(data.claims);
    } catch (error) {
      console.error('Failed to fetch pending miss claims:', error);
    }
  }, []);

  useEffect(() => {
    if (!isPartnerCoach) {
      fetchRequests();
    }
    fetchMissClaims();
    fetchCohorts();
    fetchSessions();
    fetchRecentCompleted();
  }, [isPartnerCoach, fetchRequests, fetchCohorts, fetchSessions, fetchRecentCompleted, fetchMissClaims]);

  const { items: actionItems, isLoading: isLoadingActionItems } = useCoachActionItems({
    cohorts,
    isCohortsLoading: isLoadingCohorts,
  });

  const needsAttentionItems = useNeedsAttentionItems({
    upcomingSessions: sessions,
    recentCompletedSession: recentCompleted,
    actionItems,
    missClaims,
  });

  const handleReviewAvailability = useCallback(
    (requestId: string) => {
      navigate(`/coach/availability/${requestId}`);
    },
    [navigate],
  );

  const handleCohortClick = useCallback(
    (cohortId: string) => {
      navigate(`/coach/cohorts/${cohortId}`);
    },
    [navigate],
  );

  // AC17: CTA destinations.
  // MED-940: every cohort-scoped CTA must route under /coach/cohorts/:cohortId/...
  // The bare /coach/sessions/:sessionId path is not a registered route and renders
  // a white screen.
  const handleNeedsAttentionCta = useCallback(
    (item: NeedsAttentionItem) => {
      switch (item.type) {
        case 'starts_today':
          if (item.status === 'in_progress') {
            navigate(`/coach/cohorts/${item.cohortId}/sessions/${item.sessionId}/active`);
          } else {
            navigate(`/coach/cohorts/${item.cohortId}/sessions/${item.sessionId}/prep`);
          }
          return;
        case 'overdue_attendance':
          if (item.cohortId && item.sessionId) {
            // Deep link form auto-opens the Mark Attendance modal (E24-4).
            navigate(`/coach/cohorts/${item.cohortId}?session=${item.sessionId}`);
          } else if (item.cohortId) {
            navigate(`/coach/cohorts/${item.cohortId}`);
          }
          return;
        case 'prep_needed':
          navigate(`/coach/cohorts/${item.cohortId}/sessions/${item.sessionId}/prep`);
          return;
        case 'session_report_ready':
          navigate(
            `/coach/cohorts/${item.cohortId}/sessions/${item.sessionId}/post-session`,
          );
          return;
        case 'post_session_feedback':
          // MED-867 AC2: route to the post-session screen, where the Medley
          // Feedback Form lives. Same destination as Session Report Ready (both
          // can coexist for one session, IV4); they retire independently.
          navigate(
            `/coach/cohorts/${item.cohortId}/sessions/${item.sessionId}/post-session`,
          );
          return;
        case 'connect_calendly':
        case 'calendly_booking_blocked':
          navigate('/coach/profile');
          return;
        case 'meeting_link_missing':
          // MED-1365 QA round 6: opens the profile window at the link field.
          navigate(`/coach/profile?add_link=${item.link.platform}`);
          return;
        case 'calendly_20min_unmapped':
          // MED-1365 QA round 9: opens the event type mapping window.
          navigate('/coach/profile?map_calendly=1');
          return;
        case 'feedback':
          navigate(`/coach/cohorts/${item.cohortId}?survey=pending&open=true`);
          return;
        case 'miss_claim':
          openMissClaimQuestion(item.claim, fetchMissClaims);
          return;
      }
    },
    [navigate, fetchMissClaims],
  );

  const isRailLoading =
    isLoadingSessions || isLoadingRecent || isLoadingActionItems || isLoadingCohorts;
  // MED-1394: render every panel together once all data has settled, so a
  // panel that loads later never pushes the panels below it.
  const isBodyLoading = isRailLoading || (!isPartnerCoach && isLoadingRequests);

  return (
    <Container size="xl" py="xl">
      <Box mb="xl">
        <Title order={2} fw={600}>
          Welcome back, {firstName}
        </Title>
        <Text c="dimmed" size="lg">
          Here's what's happening with your coaching schedule
        </Text>
      </Box>

      {isBodyLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
      <Stack gap="lg">
        <NeedsAttentionRail
          items={needsAttentionItems}
          isLoading={isRailLoading}
          onCardCta={handleNeedsAttentionCta}
        />

        <ActiveCohortsCompactList
          cohorts={cohorts}
          isLoading={isLoadingCohorts}
          onCohortClick={handleCohortClick}
        />

        <Grid gutter="lg">
          {!isPartnerCoach && (
            <Grid.Col span={{ base: 12, md: 5 }}>
              <AvailabilityRequestsPanel
                requests={requests}
                isLoading={isLoadingRequests}
                onReview={handleReviewAvailability}
              />
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 12, md: isPartnerCoach ? 12 : 7 }}>
            <UpcomingSessionsPanel sessions={sessions} isLoading={isLoadingSessions} />
          </Grid.Col>
        </Grid>
      </Stack>
      )}
    </Container>
  );
}

export default CoachDashboard;
